import { cn } from "@etudescarre/ui";
import { isNil } from "@etudescarre/utils";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { toaster } from "@/app";
import { confirmDialog } from "@/components/confirm-dialog";
import { apiClient } from "@/lib/api/client";
import { formatDay, todayLocal } from "@/lib/utils/date";

type PendingDocument = {
  versionId: number;
  fullname: string | null;
  hasNative: boolean;
  hasPdf: boolean;
};

type PendingItem = {
  transmissionId: number;
  slipId: number;
  bordereauNumber: string | null;
  createdAt: string;
  recipientTeamName: string | null;
  documents: PendingDocument[];
};

type FetchState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; items: PendingItem[] };

const LOAD_ERROR =
  "Erreur lors du chargement des remises en main propre en attente";

function documentsLabel(item: PendingItem): string {
  return item.documents.map((d) => d.fullname ?? "(Sans nom)").join(", ");
}

function formatsLabel(item: PendingItem): string {
  const hasNative = item.documents.some((d) => d.hasNative);
  const hasPdf = item.documents.some((d) => d.hasPdf);
  const formats: string[] = [];
  if (hasNative) formats.push("Natif");
  if (hasPdf) formats.push("PDF");
  return formats.join(" / ");
}

/**
 * Screen 021bis — "Enregistrer les remises en main propre".
 *
 * Lists the validated bordereaux (mode "en main propre") the current user
 * sees (server rule `getSlipVisibility`) that have not yet been physically
 * handed over, lets them select one or
 * more exemplaires and record the effective delivery. The delivery date and the
 * recording actor (server-side, from the session) are persisted on the
 * transmission (dateEnvoi / sentByEmployeeId).
 *
 * From the same screen, the user can also switch selected bordereaux from
 * hand delivery to postal mail ("Passer en mode courrier") after a confirmation.
 * This flips transmission.modeEnvoi to "courrier" (persisted), removing them from
 * this queue and moving them into the mises au courrier queue.
 *
 * Out of scope (no truthful data model yet): capturing a
 * recipient signature, a delivery PDF/receipt, or per-document quantities. The
 * enum "mode_envoi" already carries "en_main_propre"; delivery is tracked via
 * dateEnvoi.
 */
export function RemiseMainPropre() {
  const { yardId } = useParams<{ yardId: string }>();
  const [state, setState] = useState<FetchState>({ status: "loading" });
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [deliveredOn, setDeliveredOn] = useState<string>(todayLocal);
  const [submitting, setSubmitting] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reloadKey is intentionally used as a refetch trigger and not referenced inside the effect body
  useEffect(() => {
    if (isNil(yardId)) return;

    let cancelled = false;

    async function fetchPending() {
      setState((prev) =>
        prev.status === "success" ? prev : { status: "loading" },
      );
      // openapi-fetch rejects on transport failures (only HTTP errors come back
      // as `error`), so guard the await or the page hangs on "Chargement...".
      try {
        const { data, error } = await apiClient.GET(
          "/api/yards/{yardId}/transmissions/pending-main-propre",
          { params: { path: { yardId: yardId ?? "" } } },
        );

        if (cancelled) return;

        if (error || isNil(data)) {
          setState({ status: "error", error: LOAD_ERROR });
          return;
        }

        setState({ status: "success", items: data.data.items });
        setSelected(new Set());
      } catch {
        if (cancelled) return;
        setState({ status: "error", error: LOAD_ERROR });
      }
    }

    fetchPending();
    return () => {
      cancelled = true;
    };
  }, [yardId, reloadKey]);

  const items = state.status === "success" ? state.items : [];
  const allSelected = items.length > 0 && selected.size === items.length;

  const selectedIds = useMemo(() => [...selected], [selected]);

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelected((prev) =>
      prev.size === items.length
        ? new Set()
        : new Set(items.map((i) => i.transmissionId)),
    );
  }

  async function switchToCourrier() {
    if (isNil(yardId) || selectedIds.length === 0) return;

    // Moving a bordereau from hand-delivery to postal mail is a mode
    // change the user should confirm — it takes the bordereau out of this queue
    // and into the courrier one.
    const count = selectedIds.length;
    const confirmed = await confirmDialog({
      title: "Passer en mode courrier",
      message:
        count > 1
          ? `Passer ${count} bordereaux en mode courrier ? Ils quitteront la remise en main propre pour la file des mises au courrier.`
          : "Passer ce bordereau en mode courrier ? Il quittera la remise en main propre pour la file des mises au courrier.",
      confirmLabel: "Passer en mode courrier",
    });
    if (!confirmed) return;

    setSwitching(true);
    try {
      const { data, error } = await apiClient.POST(
        "/api/yards/{yardId}/transmissions/switch-to-courrier",
        {
          params: { path: { yardId } },
          body: { transmissionIds: selectedIds },
        },
      );

      if (error || isNil(data)) {
        toaster.error({
          title: "Erreur",
          description: "Échec du passage en mode courrier",
        });
        return;
      }

      const switched = data.data.switchedIds.length;
      toaster.success({
        title: "Passage en courrier enregistré",
        description:
          switched > 1
            ? `${switched} bordereaux passés en mode courrier`
            : "1 bordereau passé en mode courrier",
      });
      setReloadKey((k) => k + 1);
    } catch {
      toaster.error({
        title: "Erreur",
        description: "Échec du passage en mode courrier",
      });
    } finally {
      setSwitching(false);
    }
  }

  async function confirmDelivery() {
    if (isNil(yardId) || selectedIds.length === 0) return;
    setSubmitting(true);

    // Only back-date when the user picked a day other than today; today lets
    // the server stamp the exact moment (now) and dodges timezone-midnight
    // rounding that could look like a future date to the API guard.
    const deliveredAt =
      deliveredOn === todayLocal()
        ? undefined
        : new Date(`${deliveredOn}T12:00:00`).toISOString();

    try {
      const { data, error } = await apiClient.POST(
        "/api/yards/{yardId}/transmissions/mark-delivered",
        {
          params: { path: { yardId } },
          body: { transmissionIds: selectedIds, deliveredAt },
        },
      );

      if (error || isNil(data)) {
        toaster.error({
          title: "Erreur",
          description: "Échec de l'enregistrement de la remise",
        });
        return;
      }

      const count = data.data.deliveredIds.length;
      toaster.success({
        title: "Remise enregistrée",
        description:
          count > 1
            ? `${count} bordereaux remis en main propre`
            : "1 bordereau remis en main propre",
      });
      setReloadKey((k) => k + 1);
    } catch {
      toaster.error({
        title: "Erreur",
        description: "Échec de l'enregistrement de la remise",
      });
    } finally {
      setSubmitting(false);
    }
  }

  const total = items.length;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex items-end justify-between gap-4 border-b border-zinc-200 px-4 py-4 dark:border-zinc-700">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
            Enregistrer les remises en main propre
          </h2>
          {state.status === "success" && (
            <p className="mt-1 text-sm text-zinc-500">
              {total} bordereau{total > 1 ? "x" : ""} en attente de remise
            </p>
          )}
        </div>
        <div className="flex items-end gap-3">
          <button
            type="button"
            disabled={selectedIds.length === 0 || switching || submitting}
            onClick={switchToCourrier}
            className="rounded-md border border-zinc-300 px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-600 dark:text-zinc-200 dark:hover:bg-zinc-800"
          >
            {switching
              ? "Passage en courrier..."
              : `Passer en mode courrier${
                  selectedIds.length > 0 ? ` (${selectedIds.length})` : ""
                }`}
          </button>
          <label className="flex flex-col text-xs font-medium text-zinc-600 dark:text-zinc-400">
            Date de remise
            <input
              type="date"
              value={deliveredOn}
              max={todayLocal()}
              onChange={(e) => setDeliveredOn(e.target.value)}
              className="mt-1 rounded-md border border-zinc-300 px-2 py-1 text-sm text-zinc-900 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100"
            />
          </label>
          <button
            type="button"
            disabled={selectedIds.length === 0 || submitting || switching}
            onClick={confirmDelivery}
            className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-zinc-300 dark:disabled:bg-zinc-700"
          >
            {submitting
              ? "Enregistrement..."
              : `Confirmer la remise${
                  selectedIds.length > 0 ? ` (${selectedIds.length})` : ""
                }`}
          </button>
        </div>
      </div>

      {state.status === "error" ? (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-red-600">{state.error}</p>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-hidden p-4">
          <div className="h-full overflow-y-auto rounded-lg border border-zinc-200 dark:border-zinc-800">
            {/*
              Screen-design rules: fixed table layout, Documents column takes
              the remaining width, fixed row height with truncation (full text
              on hover), sticky header, only the body scrolls vertically.
            */}
            <table className="w-full table-fixed border-collapse text-sm">
              <colgroup>
                <col className="w-10" />
                <col className="w-32" />
                <col className="w-36" />
                <col className="w-48" />
                <col />
                <col className="w-32" />
              </colgroup>
              <thead className="sticky top-0 z-10 bg-zinc-50 dark:bg-zinc-900">
                <tr className="border-b border-zinc-200 dark:border-zinc-800">
                  <th className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      aria-label="Tout sélectionner"
                      checked={allSelected}
                      disabled={items.length === 0}
                      onChange={toggleAll}
                    />
                  </th>
                  <th className="truncate px-3 py-2 text-left font-medium text-zinc-700 dark:text-zinc-300">
                    Bordereau
                  </th>
                  <th
                    className="truncate px-3 py-2 text-left font-medium text-zinc-700 dark:text-zinc-300"
                    title="Date de création"
                  >
                    Date de création
                  </th>
                  <th className="truncate px-3 py-2 text-left font-medium text-zinc-700 dark:text-zinc-300">
                    Destinataire
                  </th>
                  <th className="truncate px-3 py-2 text-left font-medium text-zinc-700 dark:text-zinc-300">
                    Documents
                  </th>
                  <th className="truncate px-3 py-2 text-center font-medium text-zinc-700 dark:text-zinc-300">
                    Formats
                  </th>
                </tr>
              </thead>
              <tbody>
                {state.status === "loading" ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-3 py-8 text-center text-sm text-zinc-500"
                    >
                      Chargement...
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-3 py-12 text-center text-sm text-zinc-500"
                    >
                      Aucun bordereau en attente de remise en main propre
                    </td>
                  </tr>
                ) : (
                  items.map((item) => {
                    const documents = documentsLabel(item);
                    const formats = formatsLabel(item);
                    const checked = selected.has(item.transmissionId);
                    return (
                      <tr
                        key={item.transmissionId}
                        className="h-10 border-b border-zinc-100 last:border-0 dark:border-zinc-800"
                      >
                        <td className="px-3 text-center">
                          <input
                            type="checkbox"
                            aria-label={`Sélectionner ${
                              item.bordereauNumber ?? "bordereau"
                            }`}
                            checked={checked}
                            onChange={() => toggle(item.transmissionId)}
                          />
                        </td>
                        <td className="truncate px-3 text-left">
                          <span title={item.bordereauNumber ?? undefined}>
                            {item.bordereauNumber ?? "(Sans numéro)"}
                          </span>
                        </td>
                        <td className="truncate px-3 text-left">
                          {formatDay(item.createdAt)}
                        </td>
                        <td className="truncate px-3 text-left">
                          <span title={item.recipientTeamName ?? undefined}>
                            {item.recipientTeamName ?? "-"}
                          </span>
                        </td>
                        <td className="truncate px-3 text-left">
                          <span title={documents || undefined}>
                            {documents || "-"}
                          </span>
                        </td>
                        <td
                          className={cn(
                            "truncate whitespace-nowrap px-3 text-center",
                            formats.length === 0 && "text-zinc-300",
                          )}
                          title={formats || undefined}
                        >
                          {formats || "-"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
